import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { navigate } from 'gatsby';
import { onAuthStateChanged } from 'firebase/auth';
import type { PublicAuthUser } from '../types';
import { guestHistoryPath } from '../features/shared/invoice/invoicePaths';
import * as authApi from '../lib/auth';
import { getFirebaseAuth } from '../lib/firebase';

type AuthContextValue = {
  user: PublicAuthUser | null;
  loading: boolean;
  refresh: () => Promise<PublicAuthUser | null>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const AUTH_USER_CACHE_KEY = 'huipsov_ig_auth_user';

let cachedUser: PublicAuthUser | null | undefined;

function readStoredUser(): PublicAuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(AUTH_USER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as PublicAuthUser) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: PublicAuthUser | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (user) {
      sessionStorage.setItem(AUTH_USER_CACHE_KEY, JSON.stringify(user));
    } else {
      sessionStorage.removeItem(AUTH_USER_CACHE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function initialAuthState(): { user: PublicAuthUser | null; loading: boolean } {
  if (cachedUser !== undefined) {
    return { user: cachedUser, loading: false };
  }
  const stored = readStoredUser();
  if (stored) {
    cachedUser = stored;
    return { user: stored, loading: true };
  }
  return { user: null, loading: true };
}

/** Keep auth cache in sync before a route change remounts AuthProvider. */
export function primeAuthCache(user: PublicAuthUser): void {
  cachedUser = user;
  writeStoredUser(user);
}

export function isAuthFormPath(pathname: string): boolean {
  return (
    pathname.includes('/sign-in') ||
    pathname.includes('/signup') ||
    pathname.includes('/login') ||
    pathname.includes('/auth/google') ||
    pathname.includes('/forgot-password') ||
    pathname.includes('/reset-password')
  );
}

function goToInvoices(): void {
  if (typeof window === 'undefined') return;
  void navigate(authApi.invoicesHomePath(), { replace: true });
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authState] = useState(initialAuthState);
  const [user, setUser] = useState<PublicAuthUser | null>(() => authState.user);
  const [loading, setLoading] = useState(() => authState.loading);

  const refresh = useCallback(async (): Promise<PublicAuthUser | null> => {
    try {
      const auth = getFirebaseAuth();
      if (auth) await auth.authStateReady();
      const current = await authApi.getCurrentUser();
      cachedUser = current;
      writeStoredUser(current);
      setUser(current);
      return current;
    } catch {
      cachedUser = null;
      writeStoredUser(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) {
      cachedUser = null;
      writeStoredUser(null);
      setUser(null);
      setLoading(false);
      return;
    }

    if (cachedUser === undefined && !readStoredUser()) {
      setLoading(true);
    }
    const unsub = onAuthStateChanged(auth, () => {
      void (async () => {
        const current = await refresh();
        if (!current) return;
        if (!isAuthFormPath(window.location.pathname)) return;
        goToInvoices();
      })();
    });

    return () => {
      unsub();
    };
  }, [refresh]);

  const signOut = useCallback(async () => {
    setLoading(true);
    await authApi.signOut();
    cachedUser = null;
    writeStoredUser(null);
    setUser(null);
    void navigate(guestHistoryPath(), { replace: true });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      user: null,
      loading: false,
      refresh: async () => null,
      signOut: async () => undefined,
    };
  }
  return ctx;
}
